import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useNavHint } from "../hooks/useNav";
import { AREA_OF_TYPE, AREA_LABEL } from "./navModel";
import Switch from "./automations/Switch";
import RulesTab from "./automations/RulesTab";
import ScheduleTab from "./automations/ScheduleTab";
import TemplateTab from "./automations/TemplateTab";
import AvailabilityLink from "./AvailabilityLink";
import LogTab from "./automations/LogTab";
import RemindersTab from "./automations/RemindersTab";
import NoShowTab from "./automations/NoShowTab";
import CallSettingsTab from "./automations/CallSettingsTab";

/**
 * ONE AUTOMATION, FIVE TABS.
 *
 * The header carries the two switches and the test send; the tabs carry the
 * config. This shell owns the config draft and the save, and every tab is
 * handed `config` plus a `patch` function — so a tab never talks to the server
 * about the config and there is one place that knows whether there are unsaved
 * changes.
 *
 * WHY A DRAFT AT ALL, rather than saving each control as it moves. The threshold
 * panel and the calendar preview both take an UNSAVED config on purpose (the
 * panel is how a threshold gets chosen, and having to save to find out what it
 * does would make it useless). So the page has to hold a draft regardless, and
 * once it does, an explicit save is the honest control: a slider that writes on
 * every pixel would put forty versions of a scoring table through the database
 * on the way to the one he meant.
 *
 * THE TWO SWITCHES SAVE IMMEDIATELY, and they are the exception for the same
 * reason: they are not config, they are the state of the world. `enabled` and
 * `dry_run` round-trip on their own PATCH the moment they are clicked, which is
 * also what lets the index row toggle one without stamping on somebody's
 * unsaved config.
 */

const TABS = [
  { key: "rules", label: "Rules" },
  { key: "schedule", label: "Schedule" },
  { key: "template", label: "Template" },
  { key: "log", label: "Log" },
];

/*
 * THE TABS ABOVE ARE THE AFTER-HOURS AUTO-REPLY'S, and every one of them reads
 * that trigger's config by name — `config.scoring.threshold`, `config.schedule`,
 * the 7x24 grid. The call automations hold different documents with none of
 * those keys, so rendering those tabs against one is not a blank tab, it is a
 * TypeError and a white page.
 *
 * So the tab list is asked per trigger. Each trigger's tabs follow the shape of
 * its own config, because the config's shape is the argument: reminders are
 * sequences chosen by which call it is, and a rescue is one message gated on
 * what the evidence says. Both keep the Log, which is the one tab that is
 * type-agnostic — it reads auto_reply_logs, which every automation writes.
 *
 * The Calendar tab appears for the rescue and not for the reminders, and that is
 * not an oversight: the rescue OFFERS TIMES, from the same slot finder the
 * after-hours reply uses and with the same settings, and a reminder about a
 * booked call proposes nothing.
 */
const TABS_BY_TYPE = {
  after_hours_auto_reply: TABS,
  call_reminders: [
    { key: "reminders", label: "Reminders" },
    { key: "settings", label: "Settings" },
    { key: "log", label: "Log" },
  ],
  call_no_show: [
    { key: "rescue", label: "Rescue" },
    { key: "settings", label: "Settings" },
    { key: "log", label: "Log" },
  ],
};

// The triggers whose emails offer times, and so read Meetings ▸ Availability.
const OFFERS_TIMES = new Set(["after_hours_auto_reply", "call_no_show"]);

function tabsFor(type) {
  return TABS_BY_TYPE[type] || TABS;
}

/*
 * THE SENTENCE UNDER THE DRY-RUN SWITCH, which has to be about THIS trigger.
 *
 * It used to say "every lead runs the whole pipeline — armed hours, score, the
 * wait" for all of them, which is a description of the after-hours reply and of
 * nothing else. On a call-reminders page it named a score that is never
 * computed and a wait that does not exist, which is worse than saying nothing:
 * the whole job of this line is to tell him what dry run is currently doing, and
 * being wrong about that is how somebody turns it off expecting one thing.
 */
function blurb(automation) {
  const dry = automation.dry_run;
  switch (automation.type) {
    case "call_reminders":
      return dry
        ? "Reminders are planned for the calls already on your calendar, rendered in full, and written to the Log without being sent. Read a few before you turn this off."
        : "Calls on your calendar get their reminders from your account, unattended, at the times you set. The Log tab is the record.";
    case "call_no_show":
      return dry
        ? "Calls nobody came to are judged, the rescue is rendered with real times from your calendar, and the Log records it without sending. Read a few before you turn this off."
        : "A call nobody came to gets one email from your account offering new times. The Log tab is the record.";
    default:
      return dry
        ? "Every lead runs the whole pipeline — armed hours, score, the wait, the calendar, the template — and the Log tab records exactly what would have gone out. Read a few of those before you turn this off."
        : "Leads that clear the threshold get an email from your account, unattended, a few minutes after they arrive. The Log tab is the record.";
  }
}

export default function AutomationDetailPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get("tab");

  const [meta, setMeta] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // The address is /automations/:id whatever the trigger, so the page tells the
  // menu which area it belongs to once it knows its own type.
  const area = automation ? AREA_OF_TYPE[automation.type] : null;
  useNavHint(area ? `${area}/automations` : null);
  /*
   * WHICH REMINDER THE TEST SEND RENDERS. Lifted here for the reason the lead
   * picker is: the button lives in the header and the thing it is about lives
   * in the tab below, and a button that always sent the first reminder would be
   * useless for the copy somebody is actually editing.
   */
  const [testPick, setTestPick] = useState({ sequenceIndex: 0, stepIndex: 0 });
  const [switching, setSwitching] = useState(false);
  const [flash, setFlash] = useState(null);
  /*
   * WHICH LEAD THE TEMPLATE IS BEING TESTED ON. Lifted to the page rather than
   * held in the tab, because two things use it: the preview renders against it
   * and the test send emails that same rendering to him. A picker in the tab
   * and a button in the header that disagreed about the lead would make the
   * test prove nothing.
   *
   * Null means "the most recent lead", which is what both did before there was
   * a picker.
   */
  const [testContactId, setTestContactId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api(`/automations/${id}`);
      setMeta(data);
      setAutomation(data.automation);
      setDraft(data.automation.config);
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  /*
   * A shallow merge per section, which is all any tab needs: every control lives
   * under exactly one of scoring / schedule / template / calendar / limits, and
   * a tab that could reach across sections would be a tab that can undo another
   * tab's unsaved edit.
   */
  /*
   * PATCH ONE SECTION OF THE CONFIG.
   *
   * The merge is what every tab wants for an object section — `patch("template",
   * { thread })` must not drop the signature setting beside it. But two of the
   * call automations' sections are NOT objects: `sequences` is an array and
   * `evidence` is a bare string, and spreading either into an object is silent
   * and fatal. An array becomes `{0: …, 1: …}`, which still renders until
   * something calls `.map` on it and the page goes white; a string becomes a map
   * of its own characters.
   *
   * So anything that is not a plain object replaces the section outright, which
   * is the only sensible reading of `patch("evidence", "transcript")` anyway.
   */
  const patch = useCallback((section, values) => {
    setDraft((current) => ({
      ...current,
      [section]:
        values === null || Array.isArray(values) || typeof values !== "object"
          ? values
          : { ...current[section], ...values },
    }));
  }, []);

  const dirty = useMemo(
    () => !!draft && !!automation && JSON.stringify(draft) !== JSON.stringify(automation.config),
    [draft, automation]
  );

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await api(`/automations/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ config: draft }),
      });
      setAutomation(updated);
      setDraft(updated.config);
      setFlash("Saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setSwitchValue = async (key, value) => {
    setSwitching(true);
    setError(null);
    try {
      const updated = await api(`/automations/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ [key]: value }),
      });
      // The config is deliberately NOT taken from the response here: an unsaved
      // draft must survive flipping a switch.
      setAutomation((current) => ({ ...current, enabled: updated.enabled, dry_run: updated.dry_run }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => {
    if (!flash) return undefined;
    const timer = setTimeout(() => setFlash(null), 2200);
    return () => clearTimeout(timer);
  }, [flash]);

  if (error && !automation) {
    return (
      <div className="crm-auto">
        <div className="crm-card crm-card--warning">{error}</div>
      </div>
    );
  }
  if (!automation || !draft) {
    return (
      <div className="crm-auto">
        <p className="crm-hint">Loading…</p>
      </div>
    );
  }

  /*
   * Which tabs this trigger has, and therefore which one is showing. Derived
   * here rather than at the top of the component because it depends on the
   * automation's type, which is not known until it has loaded — and a default
   * of "rules" for a trigger that has no Rules tab is the white page this
   * whole arrangement exists to avoid.
   */
  const tabs = tabsFor(automation.type);
  // The Calendar tab moved to Meetings ▸ Availability; an old link to it lands
  // there rather than on whichever tab happens to be first.
  if (requestedTab === "calendar") return <Navigate to="/meetings/availability" replace />;
  const tab = tabs.some((t) => t.key === requestedTab) ? requestedTab : tabs[0].key;

  const status = automation.enabled
    ? { key: "active", label: "Active" }
    : { key: "draft", label: "Draft" };

  return (
    <div className="crm-auto crm-auto--detail">
      <nav className="crm-auto-crumb">
        {area && AREA_LABEL[area] && (
          <>
            <span>{AREA_LABEL[area]}</span>
            <span aria-hidden="true">/</span>
          </>
        )}
        <Link to={area && area !== "sales" ? `/${area}/automations` : "/automations"}>Automations</Link>
        <span aria-hidden="true">/</span>
        <span>{automation.name}</span>
      </nav>

      <header className="crm-auto-head">
        <div className="crm-auto-head__left">
          <h1 className="crm-auto-title">{automation.name}</h1>
          <span className={`crm-pill crm-pill--${status.key}`}>{status.label}</span>
          {automation.dry_run && <span className="crm-pill crm-pill--dryrun">Dry run</span>}
        </div>

        <div className="crm-auto-head__right">
          <TestSend
            id={id}
            config={draft}
            contactId={testContactId}
            kind={automation.type}
            pick={testPick}
            onFlash={setFlash}
            onError={setError}
          />
          <label className="crm-auto-switch">
            <span>On</span>
            <Switch
              on={automation.enabled}
              pending={switching}
              label={automation.enabled ? "Switch this automation off" : "Switch this automation on"}
              onChange={(next) => setSwitchValue("enabled", next)}
            />
          </label>
        </div>
      </header>

      {/*
        THE DRY-RUN BANNER, and it is deliberately hard to miss.

        This is the switch that decides whether a stranger receives an email
        from Gabriel without him reading it first. Migration 031 ruled on
        2026-09-06 that a rule's first action would not be mail; this feature
        reverses that, and the reversal is this checkbox rather than the merge.
        So it gets a banner rather than a setting on a tab, and the copy says
        what turning it off means rather than what the field is called.
      */}
      <div className={`crm-auto-dry${automation.dry_run ? "" : " is-live"}`}>
        <div>
          <b>{automation.dry_run ? "Dry run is on. Nothing is being sent." : "This is sending for real."}</b>
          <p>{blurb(automation)}</p>
        </div>
        <label className="crm-auto-switch">
          <span>Dry run</span>
          <Switch
            on={automation.dry_run}
            pending={switching}
            label={automation.dry_run ? "Turn dry run off and start sending" : "Turn dry run on and stop sending"}
            onChange={(next) => setSwitchValue("dry_run", next)}
          />
        </label>
      </div>

      {OFFERS_TIMES.has(automation.type) && <AvailabilityLink />}

      <div className="crm-auto-tabs" role="tablist">
        {tabs.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            className={`crm-auto-tab${tab === entry.key ? " is-active" : ""}`}
            onClick={() => setParams({ tab: entry.key }, { replace: true })}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {error && <div className="crm-card crm-card--warning crm-auto-error">{error}</div>}
      {!!automation.config_errors?.length && (
        <div className="crm-card crm-card--warning crm-auto-error">
          <b>This automation is switched off until its settings are valid.</b>
          <ul>
            {automation.config_errors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="crm-auto-tabbody" role="tabpanel">
        {tab === "reminders" && (
          <RemindersTab id={id} config={draft} patch={patch} meta={meta} onPick={setTestPick} pick={testPick} />
        )}
        {tab === "rescue" && <NoShowTab id={id} config={draft} patch={patch} meta={meta} />}
        {tab === "settings" && (
          <CallSettingsTab config={draft} patch={patch} kind={automation.type} />
        )}
        {tab === "rules" && <RulesTab id={id} config={draft} patch={patch} meta={meta} />}
        {tab === "schedule" && <ScheduleTab id={id} config={draft} patch={patch} />}
        {tab === "template" && (
          <TemplateTab
            id={id}
            config={draft}
            patch={patch}
            meta={meta}
            contactId={testContactId}
            onPickContact={setTestContactId}
          />
        )}
        {tab === "log" && <LogTab id={id} kind={automation.type} settable={meta?.settable_outcomes} />}
      </div>

      {/*
        The save bar appears only when something has changed, and it says what
        would be lost. A permanent bar with a disabled button is a worse answer:
        it makes "nothing to save" and "cannot save" look the same.
      */}
      {dirty && tab !== "log" && (
        <div className="crm-auto-savebar">
          <span>Unsaved changes</span>
          <button type="button" className="crm-btn crm-btn--ghost" onClick={() => setDraft(automation.config)}>
            Discard
          </button>
          <button type="button" className="crm-btn crm-btn--primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}

      {flash && <div className="crm-auto-flash">{flash}</div>}
    </div>
  );
}

/**
 * TEST SEND — to his own inbox, and the button says so.
 *
 * THE LEAD IS A PARAMETER AND THE ADDRESS IS NOT, which is the distinction that
 * matters. `contact_id` chooses whose facts the template renders against;
 * the recipient is derived server-side from ALLOWED_USER_EMAIL or the connected
 * Gmail mailbox, both of which are him. So this button can be told which lead
 * to render, and cannot be told where to send, whatever is in the page state.
 */
function TestSend({ id, config, contactId, kind, pick, onFlash, onError }) {
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const send = async () => {
    setBusy(true);
    try {
      const result = await api(`/automations/${id}/test-send`, {
        method: "POST",
        /*
         * The reminder being looked at, for a trigger that has more than one
         * email in it. Sending the first reminder of the first sequence
         * whatever is on screen would make the button useless for exactly the
         * copy somebody is in the middle of writing.
         */
        body: JSON.stringify({
          variant: "A",
          contact_id: contactId || null,
          sequence_index: pick ? pick.sequenceIndex : 0,
          step_index: pick ? pick.stepIndex : 0,
        }),
      });
      if (mounted.current) {
        const against = result.rendered_against && result.rendered_against.name;
        onFlash(`Test sent to ${result.sent_to}${against ? `, rendered as ${against}` : ""}`);
      }
    } catch (err) {
      if (mounted.current) onError(err.message);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className="crm-btn crm-btn--secondary"
      onClick={send}
      disabled={busy}
      title={
        kind === "after_hours_auto_reply"
          ? "Renders the template against the lead chosen on the Template tab and emails it to you. Never to the lead."
          : "Renders against a real call on your calendar and emails it to you. Never to the lead."
      }
    >
      {busy ? "Sending…" : kind === "after_hours_auto_reply" ? "Test with a lead" : "Send me a test"}
    </button>
  );
}
